import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const t = localStorage.getItem("co_token");
    if (!t) {
      setUser(null);
      setWorkspace(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      setWorkspace(r.data.workspace);
    } catch (err) {
      // If a stale impersonation header caused the failure, clear it and retry once.
      const staleImp = localStorage.getItem("co_impersonate_ws");
      if (staleImp && [403, 404].includes(err?.response?.status)) {
        localStorage.removeItem("co_impersonate_ws");
        localStorage.removeItem("co_impersonate_ws_name");
        try {
          const r2 = await api.get("/auth/me");
          setUser(r2.data.user);
          setWorkspace(r2.data.workspace);
          return;
        } catch {
          /* fall through to clear token */
        }
      }
      localStorage.removeItem("co_token");
      setUser(null);
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (email, password) => {
    // Clear any stale impersonation from a previous session before starting fresh
    localStorage.removeItem("co_impersonate_ws");
    localStorage.removeItem("co_impersonate_ws_name");
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("co_token", r.data.token);
    setUser(r.data.user);
    setWorkspace(r.data.workspace);
    return r.data;
  };

  const loginRedirectPath = (u) =>
    u?.role === "platform_admin" ? "/app/admin/workspaces" : "/app/dashboard";

  const signup = async (payload) => {
    localStorage.removeItem("co_impersonate_ws");
    localStorage.removeItem("co_impersonate_ws_name");
    const r = await api.post("/auth/signup", payload);
    localStorage.setItem("co_token", r.data.token);
    setUser(r.data.user);
    setWorkspace(r.data.workspace);
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem("co_token");
    localStorage.removeItem("co_impersonate_ws");
    localStorage.removeItem("co_impersonate_ws_name");
    setUser(null);
    setWorkspace(null);
  };

  return (
    <AuthCtx.Provider value={{ user, workspace, loading, login, signup, logout, refresh, setWorkspace, loginRedirectPath }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
